import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Plus, X } from 'lucide-react'
import { api } from '../services/api'
import Button from './Button'
import Input from './Input'

export default function CategoryTranslationBanner({
  category,
  locale,
  languageName,
  onTranslationCreated,
}) {
  const [showModal, setShowModal] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const queryClient = useQueryClient()

  const createTranslationMutation = useMutation({
    mutationFn: (data) =>
      api.put(`/categories/${category.id}`, {
        slug: category.slug,
        type: category.type,
        icon: category.icon,
        color: category.color,
        is_active: category.is_active,
        sort_order: category.sort_order,
        parent_id: category.parent_id,
        translations: [
          ...(category.translations || []),
          {
            locale,
            name: data.name,
            description: data.description,
            meta_description: '',
          },
        ],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['category', category.id.toString()],
      })
      queryClient.invalidateQueries({
        queryKey: ['categories'],
      })
      setShowModal(false)
      setName('')
      setDescription('')
      setError('')
      if (onTranslationCreated) {
        onTranslationCreated()
      }
    },
    onError: (err) => {
      const message =
        err.response?.data?.message || 'Failed to create translation'
      setError(message)
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError('Name is required')
      return
    }

    createTranslationMutation.mutate({
      name: name.trim(),
      description: description.trim(),
    })
  }

  return (
    <>
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700">
              No <strong>{languageName}</strong> translation exists for this
              category. Content is visible but read-only until you add a
              translation.
            </p>
          </div>
          <div className="ml-4">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowModal(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add {languageName} Translation
            </Button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Add {languageName} Translation
              </h3>
              <button
                onClick={() => {
                  setShowModal(false)
                  setError('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter category name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Category description"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false)
                    setError('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createTranslationMutation.isPending}
                >
                  {createTranslationMutation.isPending
                    ? 'Creating...'
                    : 'Create Translation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
